import { IonInput } from "@ionic/react";
import React, { useState, useRef, useEffect } from "react";

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  placeholder?: string;
}

export default function TimePicker({
  value,
  onChange,
  placeholder,
}: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [period, setPeriod] = useState<"AM" | "PM">("AM");
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const [selectedMinute, setSelectedMinute] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Generate hours (1-12)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);

  // Generate minutes (00-59)
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleHourClick = (hour: number) => {
    setSelectedHour(hour);
    updateTime(hour, selectedMinute ?? 0, period);
  };

  const handleMinuteClick = (minute: number) => {
    setSelectedMinute(minute);
    updateTime(selectedHour ?? 1, minute, period);
  };

  const handlePeriodClick = (newPeriod: "AM" | "PM") => {
    setPeriod(newPeriod);
    updateTime(selectedHour ?? 1, selectedMinute ?? 0, newPeriod);
  };

  const updateTime = (
    hour: number,
    minute: number,
    periodValue: "AM" | "PM"
  ) => {
    const formattedHour = hour.toString().padStart(2, "0");
    const formattedMinute = minute.toString().padStart(2, "0");
    onChange(`${formattedHour}:${formattedMinute} ${periodValue}`);
  };

  const formatNumber = (num: number): string => {
    return num.toString().padStart(2, "0");
  };

  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);

  const scrollToSelected = () => {
    requestAnimationFrame(() => {
      // Scroll to selected hour
      const hourElement = hourRef.current?.querySelector(
        `[data-hour="${selectedHour}"]`
      );
      if (hourElement) {
        hourElement.scrollIntoView({ block: "center" });
      }

      // Scroll to selected minute
      const minuteElement = minuteRef.current?.querySelector(
        `[data-minute="${selectedMinute}"]`
      );
      if (minuteElement) {
        minuteElement.scrollIntoView({ block: "center" });
      }
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Add effect to scroll to selected time when picker opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(scrollToSelected, 100); // Small delay to ensure popup is rendered
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleInputClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="time-picker-container" ref={containerRef}>
      <IonInput
        className="input-field"
        type="text"
        placeholder={placeholder}
        readonly={true}
        onClick={handleInputClick}
        value={
          selectedHour !== null && selectedMinute !== null
            ? `${formatNumber(selectedHour)}:${formatNumber(
                selectedMinute
              )} ${period}`
            : value || placeholder || "HH:MM"
        }
      ></IonInput>

      {isOpen && (
        <>
          <div className="overlay" onClick={() => setIsOpen(false)} />
          <div className="time-popover">
            <div className="ampm">
              <button
                className={`am-btn ${period === "AM" ? "active" : ""}`}
                onClick={() => handlePeriodClick("AM")}
              >
                AM
              </button>
              <button
                className={`pm-btn ${period === "PM" ? "active" : ""}`}
                onClick={() => handlePeriodClick("PM")}
              >
                PM
              </button>
            </div>
            <div className="time-picker">
              <div className="column">
                <h4>Hour</h4>
                <div className="scroll-container" ref={hourRef}>
                  {hours.map((hour) => (
                    <div
                      key={hour}
                      data-hour={hour}
                      className={`item ${
                        selectedHour === hour ? "active" : ""
                      }`}
                      onClick={() => handleHourClick(hour)}
                    >
                      {formatNumber(hour)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="time-separator">:</div>
              <div className="column">
                <h4>Minute</h4>
                <div className="scroll-container" ref={minuteRef}>
                  {minutes.map((minute) => (
                    <div
                      key={minute}
                      data-minute={minute}
                      className={`item ${
                        selectedMinute === minute ? "active" : ""
                      }`}
                      onClick={() => handleMinuteClick(minute)}
                    >
                      {formatNumber(minute)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
